// ============================================================================
// AdminEval — UI for the callback-recall eval harness (Phase 1).
//
// Thin surface: pick a fixture, click Run, render the returned markdown.
// The heavy lifting happens server-side; this page just triggers and
// displays. Later phases can add:
//   • saved runs / diff view across phases
//   • config toggles (retrieval on/off, rerank on/off)
//   • per-query filtering and drill-down
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { getAdminEvalFixtures, runAdminEvalFixture } from '../../api';
import type { AdminEvalFixture } from '../../api';

export function AdminEval() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [fixtures, setFixtures] = useState<AdminEvalFixture[]>([]);
  const [loadingFixtures, setLoadingFixtures] = useState(true);
  const [selectedFixture, setSelectedFixture] = useState<string>('');
  const [label, setLabel] = useState('baseline-pre-rag');
  const [maxQueries, setMaxQueries] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [runMeta, setRunMeta] = useState<{ fixtureId: string; label: string; startedAt: string } | null>(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      loadFixtures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const loadFixtures = async () => {
    try {
      setLoadingFixtures(true);
      const res = await getAdminEvalFixtures();
      setFixtures(res.fixtures);
      if (res.fixtures.length > 0 && !selectedFixture) {
        setSelectedFixture(res.fixtures[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fixtures');
    } finally {
      setLoadingFixtures(false);
    }
  };

  const handleRun = async () => {
    if (!selectedFixture) return;
    setRunning(true);
    setError(null);
    setMarkdown(null);
    const startedAt = new Date().toISOString();
    try {
      const res = await runAdminEvalFixture(selectedFixture, {
        label: label || undefined,
        maxQueries: maxQueries ? Number(maxQueries) : undefined,
      });
      setMarkdown(res.markdown);
      setRunMeta({ fixtureId: selectedFixture, label, startedAt });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <AdminLayout
      title="Eval Harness"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Eval' }]}
    >
      <div className="space-y-4">
        <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-dark-50 mb-1">Callback-recall eval</h3>
          <p className="text-sm text-dark-300 mb-4">
            Runs a scripted fixture through the current context-assembly path and scores callback-recall with a cross-family LLM judge + string-match. Baseline for Phase 2 (T3 retrieval) + later phases.
          </p>

          {loadingFixtures ? (
            <div className="spinner w-5 h-5" />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-dark-400 mb-1">Fixture</label>
                  <select
                    value={selectedFixture}
                    onChange={(e) => setSelectedFixture(e.target.value)}
                    className="w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50 text-sm"
                    disabled={running}
                  >
                    {fixtures.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.id} ({f.queryCount} queries)
                      </option>
                    ))}
                  </select>
                  {fixtures.find((f) => f.id === selectedFixture)?.description && (
                    <p className="text-xs text-dark-400 mt-1">
                      {fixtures.find((f) => f.id === selectedFixture)!.description}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-dark-400 mb-1">Config label</label>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="baseline-pre-rag"
                    className="w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50 text-sm"
                    disabled={running}
                  />
                </div>
                <div>
                  <label className="block text-xs text-dark-400 mb-1">Max queries (blank = all)</label>
                  <input
                    type="number"
                    value={maxQueries}
                    onChange={(e) => setMaxQueries(e.target.value)}
                    placeholder="e.g. 2 for quick iteration"
                    className="w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50 text-sm"
                    disabled={running}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={running || !selectedFixture}
                  className="bg-primary-600 hover:bg-primary-500 disabled:bg-dark-600 disabled:text-dark-400 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  {running ? 'Running…' : 'Run eval'}
                </button>
                {running && (
                  <span className="text-xs text-dark-400">
                    Burns real LLM credits — one generator call + one judge call per query.
                  </span>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 border-l-2 border-red-400 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}
        </div>

        {markdown && runMeta && (
          <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-dark-50">Report</h3>
                <p className="text-xs text-dark-400">
                  <code>{runMeta.fixtureId}</code> · label <code>{runMeta.label}</code> · started {new Date(runMeta.startedAt).toLocaleString()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(markdown)}
                className="text-xs bg-dark-700 hover:bg-dark-600 text-dark-50 px-3 py-1.5 rounded border border-dark-600"
              >
                Copy markdown
              </button>
            </div>
            <pre className="text-xs text-dark-100 whitespace-pre-wrap leading-relaxed bg-dark-900 border border-dark-700 rounded p-4 overflow-x-auto">
              {markdown}
            </pre>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
